import React, { useState, useEffect, useCallback } from 'react';
import { fetchAuditLogs } from '../../../services/auditLogService';
import Icon from '../../../components/AppIcon';

const ACTION_LABELS = {
  admin_login: { label: 'Login', color: 'text-success', bg: 'bg-success-50', icon: 'LogIn' },
  admin_logout: { label: 'Logout', color: 'text-text-secondary', bg: 'bg-surface-hover', icon: 'LogOut' },
  admin_login_failed: { label: 'Login Failed', color: 'text-error', bg: 'bg-error-50', icon: 'AlertCircle' },
  export_dashboard: { label: 'Export', color: 'text-primary', bg: 'bg-primary-50', icon: 'Download' },
  settings_change: { label: 'Settings Changed', color: 'text-warning', bg: 'bg-warning-50', icon: 'Settings' },
  maintenance_mode_enabled: { label: 'Maintenance ON', color: 'text-error', bg: 'bg-error-50', icon: 'AlertTriangle' },
  maintenance_mode_disabled: { label: 'Maintenance OFF', color: 'text-success', bg: 'bg-success-50', icon: 'CheckCircle' },
  user_edit: { label: 'User Edited', color: 'text-primary', bg: 'bg-primary-50', icon: 'UserCog' },
  user_deactivate: { label: 'User Deactivated', color: 'text-error', bg: 'bg-error-50', icon: 'UserX' },
  user_activate: { label: 'User Activated', color: 'text-success', bg: 'bg-success-50', icon: 'UserCheck' },
  user_role_change: { label: 'Role Changed', color: 'text-warning', bg: 'bg-warning-50', icon: 'Shield' },
  driver_approve: { label: 'Driver Approved', color: 'text-success', bg: 'bg-success-50', icon: 'CheckCircle' },
  driver_reject: { label: 'Driver Rejected', color: 'text-error', bg: 'bg-error-50', icon: 'XCircle' },
  driver_suspend: { label: 'Driver Suspended', color: 'text-warning', bg: 'bg-warning-50', icon: 'PauseCircle' },
  promo_code_created: { label: 'Promo Created', color: 'text-primary', bg: 'bg-primary-50', icon: 'Tag' },
  promo_code_updated: { label: 'Promo Updated', color: 'text-warning', bg: 'bg-warning-50', icon: 'Tag' },
  promo_code_deleted: { label: 'Promo Deleted', color: 'text-error', bg: 'bg-error-50', icon: 'Trash2' },
  ticket_resolved: { label: 'Ticket Resolved', color: 'text-success', bg: 'bg-success-50', icon: 'CheckSquare' },
  ticket_assigned: { label: 'Ticket Assigned', color: 'text-primary', bg: 'bg-primary-50', icon: 'UserPlus' },
  payout_approved: { label: 'Payout Approved', color: 'text-success', bg: 'bg-success-50', icon: 'DollarSign' },
  payout_rejected: { label: 'Payout Rejected', color: 'text-error', bg: 'bg-error-50', icon: 'XCircle' },
};

const getActionMeta = (actionType) =>
  ACTION_LABELS?.[actionType] || { label: actionType, color: 'text-text-secondary', bg: 'bg-surface-hover', icon: 'Activity' };

const formatTimestamp = (ts) => {
  if (!ts) return '—';
  const d = new Date(ts);
  return d?.toLocaleString('en-KE', {
    year: 'numeric', month: 'short', day: 'numeric',
    hour: '2-digit', minute: '2-digit', second: '2-digit',
  });
};

const FILTER_OPTIONS = [
  { value: '', label: 'All Actions' },
  { value: 'admin_login', label: 'Logins' },
  { value: 'export_dashboard', label: 'Exports' },
  { value: 'settings_change', label: 'Settings Changes' },
  { value: 'user_edit', label: 'User Edits' },
  { value: 'maintenance_mode_enabled', label: 'Maintenance Mode' },
  { value: 'driver_approve', label: 'Driver Actions' },
  { value: 'promo_code_created', label: 'Promo Actions' },
  { value: 'payout_approved', label: 'Payout Actions' },
];

const PAGE_SIZE = 25;

const AuditLogPanel = () => {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [search, setSearch] = useState('');
  const [filterAction, setFilterAction] = useState('');
  const [page, setPage] = useState(0);
  const [hasMore, setHasMore] = useState(true);
  const [totalCount, setTotalCount] = useState(0);

  const loadLogs = useCallback(async (pageNum = 0, reset = false) => {
    setLoading(true);
    setError('');
    try {
      const { logs: fetched, count } = await fetchAuditLogs({
        limit: PAGE_SIZE,
        offset: pageNum * PAGE_SIZE,
        actionType: filterAction,
        search,
      });
      setLogs(reset ? fetched : (prev) => [...prev, ...fetched]);
      setTotalCount(count || 0);
      setHasMore(fetched?.length === PAGE_SIZE);
    } catch (err) {
      setError(err?.message || 'Failed to load audit logs.');
    } finally {
      setLoading(false);
    }
  }, [filterAction, search]);

  useEffect(() => {
    setPage(0);
    loadLogs(0, true);
  }, [filterAction, search]);

  const handleLoadMore = () => {
    const nextPage = page + 1;
    setPage(nextPage);
    loadLogs(nextPage, false);
  };

  const handleRefresh = () => {
    setPage(0);
    loadLogs(0, true);
  };

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <h2 className="text-xl font-bold text-text-primary flex items-center gap-2">
            <Icon name="ClipboardList" size={22} />
            Audit Log
          </h2>
          <p className="text-sm text-text-secondary mt-0.5">
            Complete trail of all admin actions — logins, exports, settings changes, user edits
          </p>
        </div>
        <button
          onClick={handleRefresh}
          disabled={loading}
          className="btn-secondary text-sm flex items-center gap-2 self-start sm:self-auto"
        >
          <Icon name="RefreshCw" size={15} className={loading ? 'animate-spin' : ''} />
          Refresh
        </button>
      </div>

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-3">
        <div className="relative flex-1">
          <Icon name="Search" size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-text-secondary" />
          <input
            type="text"
            placeholder="Search by actor, action, or notes…"
            value={search}
            onChange={(e) => setSearch(e?.target?.value)}
            className="input-field pl-9 text-sm w-full"
          />
        </div>
        <select
          value={filterAction}
          onChange={(e) => setFilterAction(e?.target?.value)}
          className="input-field text-sm min-w-[180px]"
        >
          {FILTER_OPTIONS?.map((opt) => (
            <option key={opt?.value} value={opt?.value}>{opt?.label}</option>
          ))}
        </select>
      </div>

      {/* Stats bar */}
      <div className="flex items-center gap-4 text-sm text-text-secondary">
        <span className="flex items-center gap-1.5">
          <Icon name="Activity" size={14} />
          {loading && logs?.length === 0 ? 'Loading…' : `${logs?.length} entries shown`}
        </span>
      </div>

      {/* Error */}
      {error && (
        <div className="bg-error-50 border border-error text-error rounded-lg px-4 py-3 text-sm flex items-center gap-2">
          <Icon name="AlertCircle" size={16} />
          {error}
        </div>
      )}

      {/* Log Table */}
      <div className="bg-surface rounded-xl border border-border overflow-hidden">
        {/* Desktop Table */}
        <div className="hidden md:block overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-border bg-surface-hover">
                <th className="text-left px-4 py-3 text-text-secondary font-medium">Timestamp</th>
                <th className="text-left px-4 py-3 text-text-secondary font-medium">Actor</th>
                <th className="text-left px-4 py-3 text-text-secondary font-medium">Action</th>
                <th className="text-left px-4 py-3 text-text-secondary font-medium">Target</th>
                <th className="text-left px-4 py-3 text-text-secondary font-medium">Notes</th>
              </tr>
            </thead>
            <tbody>
              {loading && logs?.length === 0 ? (
                [...Array(8)]?.map((_, i) => (
                  <tr key={i} className="border-b border-border last:border-0">
                    {[...Array(5)]?.map((__, j) => (
                      <td key={j} className="px-4 py-3">
                        <div className="h-4 rounded bg-border animate-pulse" style={{ width: `${60 + j * 10}%` }} />
                      </td>
                    ))}
                  </tr>
                ))
              ) : logs?.length === 0 ? (
                <tr>
                  <td colSpan={5} className="px-4 py-12 text-center text-text-secondary">
                    <Icon name="ClipboardList" size={32} className="mx-auto mb-2 opacity-30" />
                    <p>No audit log entries found.</p>
                  </td>
                </tr>
              ) : (
                logs?.map((log) => {
                  const meta = getActionMeta(log?.action_type);
                  return (
                    <tr key={log?.id} className="border-b border-border last:border-0 hover:bg-surface-hover transition-colors">
                      <td className="px-4 py-3 text-text-secondary whitespace-nowrap font-mono text-xs">
                        {formatTimestamp(log?.created_at)}
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-2">
                          <div className="w-7 h-7 rounded-full bg-primary-50 flex items-center justify-center flex-shrink-0">
                            <Icon name="User" size={14} color="var(--color-primary)" />
                          </div>
                          <span className="font-medium text-text-primary truncate max-w-[120px]">
                            {log?.actor_name || 'Admin'}
                          </span>
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium ${meta?.bg} ${meta?.color}`}>
                          <Icon name={meta?.icon} size={12} />
                          {meta?.label}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-text-secondary text-xs">
                        {log?.target_table ? (
                          <span className="font-mono bg-surface-hover px-1.5 py-0.5 rounded">
                            {log?.target_table}
                          </span>
                        ) : '—'}
                      </td>
                      <td className="px-4 py-3 text-text-secondary max-w-[220px] truncate">
                        {log?.notes || '—'}
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>

        {/* Mobile Cards */}
        <div className="md:hidden divide-y divide-border">
          {loading && logs?.length === 0 ? (
            [...Array(5)]?.map((_, i) => (
              <div key={i} className="p-4 space-y-2">
                <div className="h-4 w-3/4 rounded bg-border animate-pulse" />
                <div className="h-3 w-1/2 rounded bg-border animate-pulse" />
              </div>
            ))
          ) : logs?.length === 0 ? (
            <div className="p-8 text-center text-text-secondary">
              <Icon name="ClipboardList" size={28} className="mx-auto mb-2 opacity-30" />
              <p className="text-sm">No audit log entries found.</p>
            </div>
          ) : (
            logs?.map((log) => {
              const meta = getActionMeta(log?.action_type);
              return (
                <div key={log?.id} className="p-4 space-y-2">
                  <div className="flex items-center justify-between gap-2">
                    <span className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-xs font-medium ${meta?.bg} ${meta?.color}`}>
                      <Icon name={meta?.icon} size={11} />
                      {meta?.label}
                    </span>
                    <span className="text-xs text-text-secondary font-mono">{formatTimestamp(log?.created_at)}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Icon name="User" size={13} color="var(--color-text-secondary)" />
                    <span className="text-sm font-medium text-text-primary">{log?.actor_name || 'Admin'}</span>
                  </div>
                  {log?.notes && (
                    <p className="text-xs text-text-secondary">{log?.notes}</p>
                  )}
                  {log?.target_table && (
                    <span className="text-xs font-mono bg-surface-hover px-1.5 py-0.5 rounded text-text-secondary">
                      {log?.target_table}
                    </span>
                  )}
                </div>
              );
            })
          )}
        </div>

        {/* Load More */}
        {hasMore && logs?.length > 0 && (
          <div className="p-4 border-t border-border text-center">
            <button
              onClick={handleLoadMore}
              disabled={loading}
              className="btn-secondary text-sm"
            >
              {loading ? 'Loading…' : 'Load More'}
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default AuditLogPanel;
